import React, { useContext } from "react";
import { useTranslation } from "react-i18next";

import LayoutMasterDetail from "layouts/LayoutMasterDetail";
import { StoreContext } from "shared/context/store-context";
import { getCurrentLanguage } from "shared/utility/lang";
import { personelServiceRouteTypes_tr, personelServiceRouteTypes_en } from "shared/util/types";
import { noRealSchoolBlocker } from "shared/utility/blocker-conditions";
import ServiceMetricCell from "features/services/shared/ServiceMetricCell";

import BundledRouteItem from "./BundledRouteItem";
import RideSkipCountCell from "features/ride-skip/RideSkipCountCell";
import { useRideSkipSummary } from "features/ride-skip/useRideSkipSummary";
import { RIDE_SKIP_SERVICE_KINDS } from "features/ride-skip/constants";

// Bundled (multi-school) student routes — list + master/detail host.
//
// Backed by the INDEPENDENT BundledRoute collection (its own /api/bundled-route endpoints). The list
// is loaded into the global store at boot (feature-gated), so it renders instantly like vehicle
// routes instead of fetching on every mount.
const BundledRoutesList = ({ isNewItem }) => {
  const { t } = useTranslation();
  const currentLanguage = getCurrentLanguage();
  const routeTypes = currentLanguage === "tr" ? personelServiceRouteTypes_tr : personelServiceRouteTypes_en;

  const { schools, bundledRoutes, InsertBundledRoute, UpdateBundledRoute, DeleteBundledRoute } =
    useContext(StoreContext);
  const { enabled: skipEnabled, counts: skipCounts } = useRideSkipSummary();

  const items = bundledRoutes;
  const InsertItem = InsertBundledRoute;
  const UpdateItem = UpdateBundledRoute;
  const DeleteItem = DeleteBundledRoute;

  const listColumns = [
    { field: "name", headerName: t("name"), width: 300 },
    {
      field: "routeType",
      headerName: t("serviceType"),
      width: 160,
      valueGetter: (params) => routeTypes.find((item) => item.value === params.row?.routeType)?.label || "-",
    },
    {
      field: "driver",
      headerName: t("driver"),
      width: 250,
      valueGetter: (params) =>
        Array.isArray(params.row?.driver) && params.row.driver.length > 0
          ? params.row.driver.map((driver) => driver.name).join(", ")
          : "-",
    },
    {
      field: "numOfSchools",
      headerName: t("bundledRoutes.schoolCount"),
      width: 130,
      valueGetter: (params) => params.row?.schools?.length?.toString() || "-",
    },
    {
      field: "numOfStudents",
      headerName: t("numberofpassengers"),
      width: 160,
      valueGetter: (params) => params.row?.student?.length?.toString() || "-",
    },
    ...(skipEnabled
      ? [
          {
            field: "rideSkipCount",
            headerName: t("rideSkip.listColumn"),
            width: 170,
            valueGetter: (params) => skipCounts[`${RIDE_SKIP_SERVICE_KINDS.BUNDLED_ROUTE}:${params.row.id}`] || 0,
            renderCell: (params) => (
              <RideSkipCountCell count={skipCounts[`${RIDE_SKIP_SERVICE_KINDS.BUNDLED_ROUTE}:${params.row.id}`]} />
            ),
          },
        ]
      : []),
    {
      field: "routeMetrics",
      headerName: t("routeMetrics"),
      width: 180,
      valueGetter: (params) => {
        const duration = Number(params.row?.totalDuration);
        return Number.isFinite(duration) ? duration : 0;
      },
      renderCell: (params) => <ServiceMetricCell row={params.row} />,
    },
  ];

  return (
    <LayoutMasterDetail
      itemType="vehicleRoute"
      items={items}
      listName="main_bundledRoutes"
      listColumns={listColumns}
      SelectedItemComponent={BundledRouteItem}
      NewItemComponent={BundledRouteItem}
      DeleteItem={DeleteItem}
      InsertItem={InsertItem}
      UpdateItem={UpdateItem}
      isAlwaysFullScreen={true}
      hideCreateNewItemWhenSelected={true}
      isNewItem={isNewItem}
      blockerConditions={[noRealSchoolBlocker(schools)]}
    />
  );
};

export default BundledRoutesList;
